// Affiche A5 à imprimer pour le comptoir : /affiche
// Le QR d'inscription est stylisé aux couleurs du commerce, avec son logo au centre.
import { useEffect, useState } from 'react'
import QRCodeStyling from 'qr-code-styling'
import { supabase } from './supabase'
import { CORAL, CHARBON, Logo } from './actions'

const STYLES = {
  arrondi:   { label: 'Arrondi',   dots: 'rounded',        coins: 'extra-rounded', centres: 'dot' },
  points:    { label: 'Points',    dots: 'dots',           coins: 'extra-rounded', centres: 'dot' },
  classique: { label: 'Classique', dots: 'square',         coins: 'square',        centres: 'square' },
}

// Un QR trop clair ne se scanne pas : au-delà d'une certaine luminosité, on repasse en noir
function couleurLisible(hex) {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return lum > 0.45 ? CHARBON : hex
}

export default function Affiche({ session }) {
  const [commerce, setCommerce] = useState(null)
  const [style, setStyle] = useState('arrondi')
  const [qr, setQr] = useState('')
  const lien = `${window.location.origin}/c/${session.user.id}`

  useEffect(() => {
    supabase.from('commerces').select('nom, objectif_tampons, recompense, couleur, logo_url').eq('id', session.user.id).maybeSingle()
      .then(({ data }) => setCommerce(data))
  }, [session.user.id])

  useEffect(() => {
    if (!commerce) return
    const couleur = couleurLisible(commerce.couleur || CHARBON)
    const s = STYLES[style]
    const code = new QRCodeStyling({
      width: 720, height: 720, type: 'canvas', data: lien, margin: 8,
      image: commerce.logo_url || undefined,
      qrOptions: { errorCorrectionLevel: 'H' }, // tolère le logo au centre
      imageOptions: { crossOrigin: 'anonymous', margin: 8, imageSize: 0.3, hideBackgroundDots: true },
      dotsOptions: { type: s.dots, color: couleur },
      cornersSquareOptions: { type: s.coins, color: couleur },
      cornersDotOptions: { type: s.centres, color: couleur },
      backgroundOptions: { color: '#ffffff' },
    })
    let url
    code.getRawData('png').then(blob => { url = URL.createObjectURL(blob); setQr(url) })
    return () => url && URL.revokeObjectURL(url)
  }, [commerce, style, lien])

  if (!commerce || !qr) return <div style={{ padding: '2rem', color: '#bbb' }}>Chargement…</div>

  const couleur = commerce.couleur || CORAL

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: '#e9e4de', minHeight: '100vh', padding: '1.5rem 1rem' }}>
      <style>{`
        @page { size: A5 portrait; margin: 0; }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .feuille { box-shadow: none !important; margin: 0 !important; width: 148mm !important; height: 210mm !important; max-width: none !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="no-print" style={{ maxWidth: 560, margin: '0 auto 1rem', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <a href="/app" style={{ color: '#666', fontSize: 14 }}>← Tableau de bord</a>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {Object.entries(STYLES).map(([cle, s]) => (
            <button key={cle} onClick={() => setStyle(cle)} style={{
              border: 'none', borderRadius: 20, padding: '7px 12px', fontSize: 13, cursor: 'pointer',
              background: style === cle ? CHARBON : '#fff', color: style === cle ? '#fff' : '#666',
            }}>{s.label}</button>
          ))}
        </div>
        <button onClick={() => window.print()} style={{ background: CORAL, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          Imprimer (A5)
        </button>
      </div>

      <div className="feuille" style={{
        width: '100%', maxWidth: 560, aspectRatio: '148 / 210', margin: '0 auto', background: '#fff',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)', boxSizing: 'border-box', padding: '8% 9%',
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', color: CHARBON,
        borderTop: `14px solid ${couleur}`,
      }}>
        <div style={{ fontSize: 'clamp(22px, 5vw, 34px)', fontWeight: 800, lineHeight: 1.1 }}>{commerce.nom}</div>
        <div style={{ fontSize: 'clamp(14px, 3vw, 19px)', color: '#666', marginTop: 10 }}>
          Votre carte de fidélité, directement dans votre téléphone
        </div>

        <div style={{ background: couleur, color: '#fff', borderRadius: 14, padding: '10px 18px', fontSize: 'clamp(15px, 3.4vw, 22px)', fontWeight: 800, margin: '6% 0 4%' }}>
          🎁 {commerce.objectif_tampons} passages = {commerce.recompense}
        </div>

        <img src={qr} alt="QR code d'inscription" style={{ width: '60%', aspectRatio: '1' }} />

        <div style={{ fontSize: 'clamp(14px, 3vw, 18px)', fontWeight: 700, marginTop: '3%' }}>
          Scannez avec l'appareil photo
        </div>
        <div style={{ fontSize: 'clamp(12px, 2.4vw, 14px)', color: '#888', marginTop: 4 }}>
          Sans application · 30 secondes · Gratuit
        </div>

        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8, color: '#aaa', fontSize: 12 }}>
          <Logo size={20} /> jeebi
        </div>
      </div>

      <div className="no-print" style={{ maxWidth: 560, margin: '1rem auto 0', fontSize: 12, color: '#888', lineHeight: 1.6 }}>
        Avant d'imprimer : scanne le QR avec 2 téléphones différents pour vérifier qu'il fonctionne.
        <br />Lien de la page client : <a href={lien} style={{ color: '#666', wordBreak: 'break-all' }}>{lien}</a>
      </div>
    </div>
  )
}
